// ============================================================================
// buildCompensationPayload — garde P-ADM-16 (AD-D Admin remuneration)
// ============================================================================
// [!] P-ADM-16 — PIEGE CENTRAL : setCompensationRecord (compensation.ts) fait un
// UPDATE PLEIN de class_id / echelon / niveau / hire_date / off_grid /
// off_grid_reason_private / off_grid_reason_public / role_fit_flag /
// role_fit_note. Un PUT partiel qui omet les raisons / la note les ECRASE a
// NULL (effacement silencieux).
//
// Contrat : le formulaire DOIT s'hydrater depuis getManagementCompensationView
// (DTO ManagementCompensationRecord) AVANT toute soumission. Ce helper exige
// le record hydrate et REPORTE off_grid_reason_* / role_fit_note lorsqu'un
// edit partiel ne les touche pas (undefined = non touche, pas « vider »).
//
// Distinction AD-D vs AD-C :
//   AD-C = portes ADMIN-WIDE (createEmployeeProfile, setEmployeeEmployed…)
//   AD-D = par-cible : REUTILISE /api/management/:email/* (grant canManageEmployee,
//          admin deja OK) — JAMAIS un 2e chemin sous /api/admin (FF-PII-05).

import type {
  CompensationPayload,
  ManagementCompensationRecord,
} from '../../hooks/useManagement';

/**
 * Champs toujours fournis par le formulaire (classe / echelon / drapeaux).
 * Les raisons privees/publiques et la note role_fit sont OPTIONNELLES :
 * absentes (= undefined) => reportees depuis `hydrated` (P-ADM-16).
 * Presentes (= string | null) => valeur explicite du formulaire (y compris clear).
 */
export interface CompensationFormEdits {
  classId: string;
  currentEchelon: number | null;
  currentNiveau: number | null;
  hireDate: string | null;
  offGrid: boolean;
  offGridReasonPrivate?: string | null;
  offGridReasonPublic?: string | null;
  roleFitFlag: boolean;
  roleFitNote?: string | null;
}

function resolveOptionalText(
  touched: string | null | undefined,
  hydratedValue: string | null | undefined,
): string | null {
  if (touched !== undefined) {
    const trimmed = touched?.trim() ?? '';
    return trimmed.length > 0 ? trimmed : null;
  }
  return hydratedValue ?? null;
}

/**
 * Construit le payload PUT /api/management/:email/compensation.
 * NOTE: si offGrid=false, les raisons sont forcees a null (M-752, aussi serveur).
 * Si roleFitFlag=false, la note est forcee a null.
 */
export function buildCompensationPayload(
  hydrated: ManagementCompensationRecord | null,
  edits: CompensationFormEdits,
): CompensationPayload {
  const offGrid = edits.offGrid;
  const roleFitFlag = edits.roleFitFlag;

  return {
    classId: edits.classId,
    currentEchelon: edits.currentEchelon,
    currentNiveau: edits.currentNiveau,
    hireDate: edits.hireDate,
    offGrid,
    // P-ADM-16 : ne jamais laisser ces champs « tomber » a null par oubli.
    offGridReasonPrivate: offGrid
      ? resolveOptionalText(edits.offGridReasonPrivate, hydrated?.offGridReasonPrivate)
      : null,
    offGridReasonPublic: offGrid
      ? resolveOptionalText(edits.offGridReasonPublic, hydrated?.offGridReasonPublic)
      : null,
    roleFitFlag,
    roleFitNote: roleFitFlag
      ? resolveOptionalText(edits.roleFitNote, hydrated?.roleFitNote)
      : null,
  };
}
